import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Paper,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Chip,
  Alert,
  Card,
  CardMedia,
  CardActions,
  Skeleton,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
  Fade,
  Zoom,
  Tooltip,
  Badge,
} from "@mui/material";
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  PhotoCamera as PhotoCameraIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import SideBar from "../dashboard admin/components/SideBar";
import api from "../../../services/api/api";
import { useParams, useNavigate } from "react-router-dom";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import Swal from "sweetalert2";

const EditNewsAdmin = () => {
  const { newsId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // Estados principais
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    category: "",
    isPublished: false,
  });

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Estados para imagens
  const [images, setImages] = useState({
    existingCapa: null,
    existingOthers: [],
    newCapa: null,
    newOthers: [],
    toRemove: [],
  });

  // Estado para preview
  const [preview, setPreview] = useState({
    open: false,
    src: "",
    title: "",
  });

  const getAuthToken = () => localStorage.getItem("token");

  // Carregar dados da notícia
  const loadNewsData = useCallback(async () => {
    const token = getAuthToken();
    if (!token || !newsId) return;

    try {
      setLoading(true);
      const response = await api.get(`api/getNewsById/${newsId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const { title, content, category, isPublished, CapaImage, OthersImages } = response.data;

      setFormData({
        title: title || "",
        content: content || "",
        category: category || "",
        isPublished: isPublished || false,
      });

      setImages(prev => ({
        ...prev,
        existingCapa: CapaImage || null,
        existingOthers: OthersImages || [],
      }));
    } catch (error) {
      console.error("Erro ao buscar notícia:", error);
      Swal.fire({
        icon: "error",
        title: "Erro!",
        text: "Erro ao carregar a notícia.",
        confirmButtonColor: "#1976d2",
      });
    } finally {
      setLoading(false);
    }
  }, [newsId]);

  // Carregar categorias
  const loadCategories = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const { data } = await api.get("api/getAllCategoriesNews", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(data || []);
      
      // Atualizar categoria após carregar
      if (data?.length > 0 && formData.category) {
        const categoryObj = data.find(cat => cat.name === formData.category);
        if (categoryObj) {
          setFormData(prev => ({ ...prev, category: categoryObj._id || categoryObj.id }));
        }
      }
    } catch (error) {
      console.error("Erro ao buscar categorias:", error);
    }
  }, [formData.category]);

  useEffect(() => {
    loadNewsData();
  }, [loadNewsData]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Manipuladores de formulário
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Manipuladores de imagem
  const handleImagePreview = (src, title) => {
    setPreview({ open: true, src, title });
  };

  const handleRemoveExistingImage = (type, index = null) => {
    Swal.fire({
      title: "Confirmar remoção",
      text: "Esta imagem será removida permanentemente.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#1976d2",
      confirmButtonText: "Remover",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        if (type === 'capa') {
          setImages(prev => ({
            ...prev,
            toRemove: [...prev.toRemove, prev.existingCapa],
            existingCapa: null,
          }));
        } else {
          const imageUrl = images.existingOthers[index];
          setImages(prev => ({
            ...prev,
            toRemove: [...prev.toRemove, imageUrl],
            existingOthers: prev.existingOthers.filter((_, i) => i !== index),
          }));
        }
      }
    });
  };

  const handleNewImageUpload = (type, files) => {
    if (type === 'capa') {
      setImages(prev => ({ ...prev, newCapa: files[0] || null }));
    } else {
      setImages(prev => ({ ...prev, newOthers: Array.from(files) }));
    }
  };

  const handleRemoveNewImage = (type, index = null) => {
    if (type === 'capa') {
      setImages(prev => ({ ...prev, newCapa: null }));
    } else {
      setImages(prev => ({
        ...prev,
        newOthers: prev.newOthers.filter((_, i) => i !== index),
      }));
    }
  };

  // Submissão do formulário
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.category || !formData.content) {
      Swal.fire({
        icon: "warning",
        title: "Campos obrigatórios",
        text: "Preencha todos os campos obrigatórios.",
        confirmButtonColor: "#1976d2",
      });
      return;
    }

    const selectedCategory = categories.find(cat => (cat._id || cat.id) === formData.category);
    if (!selectedCategory) {
      Swal.fire({
        icon: "error",
        title: "Categoria inválida",
        text: "Selecione uma categoria válida.",
        confirmButtonColor: "#1976d2",
      });
      return;
    }

    const token = getAuthToken();
    if (!token) return;

    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("content", formData.content);
      formDataToSend.append("category", formData.category);
      formDataToSend.append("isPublished", formData.isPublished);

      // Imagens existentes a manter
      const imagesToKeep = images.existingOthers.filter(img => 
        img && !images.toRemove.includes(img)
      );
      if (imagesToKeep.length > 0) {
        formDataToSend.append("existingImages", JSON.stringify(imagesToKeep));
      }

      // Imagens a remover
      if (images.toRemove.length > 0) {
        formDataToSend.append("imagesToRemove", JSON.stringify(images.toRemove));
      }

      // Nova imagem de capa
      if (images.newCapa) {
        formDataToSend.append("CapaImage", images.newCapa);
      }

      // Novas outras imagens
      images.newOthers.forEach((file) => {
        formDataToSend.append("OthersImages", file);
      });

      await api.put(`api/updateNews/${newsId}`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      Swal.fire({
        icon: "success",
        title: "Sucesso!",
        text: "Notícia atualizada com sucesso!",
        confirmButtonColor: "#1976d2",
      }).then(() => {
        navigate("/admin/news");
      });
    } catch (error) {
      console.error("Erro ao atualizar notícia:", error);
      Swal.fire({
        icon: "error",
        title: "Erro!",
        text: error.response?.data?.message || "Erro ao atualizar a notícia.",
        confirmButtonColor: "#1976d2",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Componente de upload de imagem
  const ImageUploadCard = ({ title, accept, multiple = false, onUpload, children, ...props }) => (
    <Card
      variant="outlined"
      sx={{
        border: '2px dashed',
        borderColor: 'primary.light',
        bgcolor: 'background.paper',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: 'primary.main',
          bgcolor: 'action.hover',
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4],
        },
        ...props.sx,
      }}
      onClick={() => document.getElementById(props.inputId)?.click()}
    >
      <Box
        sx={{
          p: 3,
          textAlign: 'center',
          minHeight: 120,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Clique para selecionar ou arraste arquivos aqui
        </Typography>
        <input
          id={props.inputId}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={(e) => onUpload(e.target.files)}
          style={{ display: 'none' }}
        />
      </Box>
      {children}
    </Card>
  );

  // Componente de imagem existente
  const ExistingImageCard = ({ src, title, onRemove, onView, index }) => (
    <Zoom in timeout={300}>
      <Card
        sx={{
          position: 'relative',
          borderRadius: 2,
          overflow: 'hidden',
          transition: 'transform 0.2s ease',
          '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: theme.shadows[8],
          },
        }}
      >
        <CardMedia
          component="img"
          height={isMobile ? 100 : 120}
          image={src}
          alt={title}
          sx={{ cursor: 'pointer' }}
          onClick={() => onView(src, title)}
        />
        <CardActions
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            p: 0.5,
            bgcolor: 'rgba(0,0,0,0.7)',
            borderRadius: '0 0 0 8px',
          }}
        >
          <Tooltip title="Visualizar">
            <IconButton
              size="small"
              onClick={() => onView(src, title)}
              sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remover">
            <IconButton
              size="small"
              onClick={onRemove}
              sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,0,0,0.2)' } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </CardActions>
      </Card>
    </Zoom>
  );

  // Componente de nova imagem
  const NewImageCard = ({ file, title, onRemove, index }) => (
    <Zoom in timeout={300}>
      <Card
        sx={{
          position: 'relative',
          borderRadius: 2,
          overflow: 'hidden',
          border: '2px solid',
          borderColor: 'success.main',
        }}
      >
        <CardMedia
          component="img"
          height={isMobile ? 100 : 120}
          image={URL.createObjectURL(file)}
          alt={title}
        />
        <Chip
          label="Nova"
          color="success"
          size="small"
          sx={{
            position: 'absolute',
            top: 4,
            left: 4,
            fontWeight: 'bold',
          }}
        />
        <CardActions
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            p: 0.5,
            bgcolor: 'rgba(0,0,0,0.7)',
            borderRadius: '0 0 0 8px',
          }}
        >
          <Tooltip title="Remover">
            <IconButton
              size="small"
              onClick={onRemove}
              sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,0,0,0.2)' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </CardActions>
      </Card>
    </Zoom>
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
        <SideBar />
        <Box sx={{ flex: 1, p: { xs: 2, md: 4 } }}>
          <Skeleton variant="text" width="60%" height={60} sx={{ mb: 2 }} />
          <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 2 }}>
            <Stack spacing={3}>
              <Skeleton variant="rectangular" height={56} />
              <Skeleton variant="rectangular" height={200} />
              <Skeleton variant="rectangular" height={56} />
              <Skeleton variant="rectangular" height={150} />
            </Stack>
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      <SideBar />

      <Box sx={{ flex: 1, p: { xs: 2, md: 4 } }}>
        <Typography
          variant={isMobile ? "h5" : "h4"}
          fontWeight="bold"
          mb={3}
          sx={{
            background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          <EditIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Editar Notícia
        </Typography>

        <Paper
          sx={{
            p: { xs: 2, md: 4 },
            borderRadius: 3,
            boxShadow: theme.shadows[4],
            background: 'linear-gradient(135deg, rgba(255,255,255,0.9), rgba(248,250,252,0.9))',
          }}
        >
          <form onSubmit={handleSubmit}>
            <Stack spacing={4}>
              {/* Título */}
              <TextField
                label="Título da Notícia"
                variant="outlined"
                fullWidth
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />

              {/* Conteúdo */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <EditIcon sx={{ mr: 1 }} />
                  Conteúdo da Notícia
                </Typography>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    '& .ql-container': {
                      minHeight: isMobile ? 200 : 300,
                    },
                  }}
                >
                  <ReactQuill
                    value={formData.content}
                    onChange={(value) => handleInputChange('content', value)}
                    modules={{
                      toolbar: [
                        [{ header: [1, 2, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        [{ align: [] }],
                        ['link', 'image'],
                        ['clean'],
                      ],
                    }}
                  />
                </Box>
              </Box>

              {/* Categoria */}
              <TextField
                select
                label="Categoria"
                fullWidth
                variant="outlined"
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat._id || cat.id} value={cat._id || cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </TextField>

              <Divider />

              {/* Imagem de Capa */}
              <Box>
                <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                  <PhotoCameraIcon sx={{ mr: 1 }} />
                  Imagem de Capa
                </Typography>

                <Grid container spacing={2}>
                  {/* Imagem de capa existente */}
                  {images.existingCapa && (
                    <Grid item xs={12} sm={6} md={4}>
                      <ExistingImageCard
                        src={images.existingCapa}
                        title="Imagem de Capa Atual"
                        onRemove={() => handleRemoveExistingImage('capa')}
                        onView={handleImagePreview}
                      />
                    </Grid>
                  )}

                  {/* Nova imagem de capa */}
                  {images.newCapa && (
                    <Grid item xs={12} sm={6} md={4}>
                      <NewImageCard
                        file={images.newCapa}
                        title="Nova Imagem de Capa"
                        onRemove={() => handleRemoveNewImage('capa')}
                      />
                    </Grid>
                  )}

                  {/* Upload de nova capa */}
                  {!images.newCapa && (
                    <Grid item xs={12} sm={6} md={4}>
                      <ImageUploadCard
                        title={images.existingCapa ? "Substituir Capa" : "Adicionar Capa"}
                        accept="image/*"
                        onUpload={(files) => handleNewImageUpload('capa', files)}
                        inputId="capa-upload"
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>

              <Divider />

              {/* Outras Imagens */}
              <Box>
                <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                  <ImageIcon sx={{ mr: 1 }} />
                  Outras Imagens
                  <Badge
                    badgeContent={images.existingOthers.length + images.newOthers.length}
                    color="primary"
                    sx={{ ml: 2 }}
                  />
                </Typography>

                {/* Alert informativo */}
                <Alert
                  severity="info"
                  sx={{ mb: 3, borderRadius: 2 }}
                  icon={<ImageIcon />}
                >
                  As imagens existentes serão mantidas junto com as novas. 
                  Use o botão de remoção para excluir imagens específicas.
                </Alert>

                <Grid container spacing={2}>
                  {/* Imagens existentes */}
                  {images.existingOthers.map((imageUrl, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={`existing-${index}`}>
                      <ExistingImageCard
                        src={imageUrl}
                        title={`Imagem ${index + 1}`}
                        onRemove={() => handleRemoveExistingImage('others', index)}
                        onView={handleImagePreview}
                        index={index}
                      />
                    </Grid>
                  ))}

                  {/* Novas imagens */}
                  {images.newOthers.map((file, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={`new-${index}`}>
                      <NewImageCard
                        file={file}
                        title={`Nova Imagem ${index + 1}`}
                        onRemove={() => handleRemoveNewImage('others', index)}
                        index={index}
                      />
                    </Grid>
                  ))}

                  {/* Upload de outras imagens */}
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <ImageUploadCard
                      title="Adicionar Imagens"
                      accept="image/*"
                      multiple
                      onUpload={(files) => handleNewImageUpload('others', files)}
                      inputId="others-upload"
                      sx={{ minHeight: isMobile ? 120 : 160 }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Publicar */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isPublished}
                    onChange={(e) => handleInputChange('isPublished', e.target.checked)}
                    sx={{
                      '&.Mui-checked': {
                        color: 'success.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Publicar notícia imediatamente
                  </Typography>
                }
              />

              {/* Botões de ação */}
              <Stack
                direction={isMobile ? 'column' : 'row'}
                spacing={2}
                justifyContent="flex-end"
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate("/admin/news")}
                  disabled={submitting}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #1565c0, #1976d2)',
                    },
                  }}
                >
                  {submitting ? 'Atualizando...' : 'Atualizar Notícia'}
                </Button>
              </Stack>
            </Stack>
          </form>
        </Paper>
      </Box>

      {/* Modal de Preview */}
      <Dialog
        open={preview.open}
        onClose={() => setPreview({ open: false, src: "", title: "" })}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: 'primary.main',
            color: 'white',
            py: 2,
          }}
        >
          <Typography variant="h6">{preview.title}</Typography>
          <IconButton
            onClick={() => setPreview({ open: false, src: "", title: "" })}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: 'black' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '50vh',
            }}
          >
            <img
              src={preview.src}
              alt={preview.title}
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default EditNewsAdmin;